import React, { Suspense, useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  ListItemText,
  Box,
  Typography,
  ToggleButton,
  Divider,
  Slider,
} from '@mui/material';
import DawaAutocomplete from '../../../components/dawa/DawaAutocomplete';
import { darken } from '@mui/material/styles';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import styled from '@emotion/styled';
import InvertedSlider from '@/components/sliders/InvertedSlider';

function formatLocationString(locationData) {
  if (!locationData) return '';
  const { street, houseNumber, floor, door, zipCode, city } = locationData;

  const parts = [];

  // Street + house number
  if (street && houseNumber) {
    parts.push(`${street} ${houseNumber}`);
  }

  // Optional floor and door
  const floorDoor = [floor, door].filter(Boolean).join(' ');
  if (floorDoor) {
    parts.push(floorDoor);
  }

  // Zip code and city
  if (zipCode && city) {
    parts.push(`${zipCode} ${city}`);
  }

  return parts.join(', ');
}

// function parseDaysOfWeek(dayString) {
//   const allDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
//   const selected = (dayString || '').split(',').map((d) => d.trim());
//   return Object.fromEntries(allDays.map((day) => [day, selected.includes(day)]));
// }

// function serializeDaysOfWeek(daysObj) {
//   return Object.entries(daysObj)
//     .filter(([_, selected]) => selected)
//     .map(([day]) => day)
//     .join(',') || null;
// }

const EditEnergyGroupDialog = ({
  open,
  onClose,
  triggerGetLocation,
  selectedEnergyGroup,
  locationData,
  onUpdate,
}) => {
  const [range, setRange] = useState([6, 18]); // Normal default
  // const [submittedRange, setSubmittedRange] = useState([6, 22]);
  const [isInverted, setIsInverted] = useState(false);

  const [updatedEnergyGroup, setUpdatedEnergyGroup] = useState({
    name: selectedEnergyGroup?.name,
    energyGroupId: selectedEnergyGroup?.energyGroupId,
    accountId: selectedEnergyGroup?.accountId,
    locationId: locationData?.locationId,
    energyInitiatives: selectedEnergyGroup?.energyInitiatives || [],
    options: selectedEnergyGroup?.options,
  });

  useEffect(() => {
    if (selectedEnergyGroup) {
      setUpdatedEnergyGroup({
        name: selectedEnergyGroup.name,
        accountId: selectedEnergyGroup.accountId,
        locationId: locationData?.locationId,
        energyInitiatives: selectedEnergyGroup.energyInitiatives || [],
        options: {
          ...selectedEnergyGroup.options,
          daysOfWeek: selectedEnergyGroup.options?.daysOfWeek,
          isDraftMode: selectedEnergyGroup.options?.isDraftMode,
          workingDayStart: selectedEnergyGroup.options?.workingDayStart || 6,
          workingDayStart: selectedEnergyGroup.options?.workingDayStart || 18,
        },
      });
      setRange([
        selectedEnergyGroup.options?.workingDayStart || 6,
        selectedEnergyGroup.options?.workingDayEnd || 18,
      ]);
    }
  }, [selectedEnergyGroup, locationData]);

  const handleChange = (event, newValue) => {
    setRange(newValue);
    setUpdatedEnergyGroup((prev) => ({
      ...prev,
      options: {
        ...prev.options,
        workingDayStart: newValue[0],
        workingDayEnd: newValue[1],
      },
    }));
  };

  const formatHour = (value) => {
    const hour = Math.floor(value);
    const remainder = (value % 1).toFixed(2);

    const minuteMap = {
      '0.00': ':00',
      0.25: ':15',
      '0.50': ':30',
      0.75: ':45',
    };

    const minutes = minuteMap[remainder] || ':00';
    return `${hour}${minutes}`;
  };

  const handleDayToggle = (dayKey) => {
    const current = updatedEnergyGroup.options?.daysOfWeek || {};
    const updated = {
      ...current,
      [dayKey]: !current[dayKey],
    };
    setUpdatedEnergyGroup((prev) => ({
      ...prev,
      options: {
        ...prev.options,
        daysOfWeek: updated,
      },
    }));
  };

  const isAnyDaySelected = Object.values(
    updatedEnergyGroup.options?.daysOfWeek || {},
  ).some(Boolean);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        elevation: 0,
        sx: {
          backgroundColor: (theme) =>
            theme.palette.mode === 'dark' ? '#000' : '#fff',
          border: '1px solid',
          borderColor: (theme) => theme.palette.divider,
          borderRadius: '8px',
        },
      }}
    >
      <DialogTitle>Rediger {selectedEnergyGroup?.name}</DialogTitle>
      <DialogContent dividers>
        <TextField
          label="Navn"
          value={updatedEnergyGroup?.name}
          required
          error={!updatedEnergyGroup?.name}
          onChange={(e) => {
            setUpdatedEnergyGroup((prev) => ({
              ...prev,
              name: e.target.value,
            }));
          }}
          fullWidth
          variant="filled"
          helperText="Giv gruppen et sigende navn"
          InputProps={{ disableUnderline: true }}
        />
        <Suspense fallback={<div>Loading LocationSearchInput...</div>}>
          <DawaAutocomplete
            onLocationSelect={async (location) => {
              if (location && location.data) {
                try {
                  const result = await triggerGetLocation(
                    location.data.id,
                  ).unwrap();
                  if (result) {
                    const locationData = {
                      address: result.adressebetegnelse,
                      latitude:
                        result.adgangsadresse.adgangspunkt.koordinater[1],
                      longitude:
                        result.adgangsadresse.adgangspunkt.koordinater[0],
                      zip: parseInt(result.adgangsadresse.postnummer.nr),
                      tz: 'Europe/Copenhagen',
                      location_code:
                        parseInt(result.adgangsadresse.postnummer.nr) < 5000
                          ? 'DK2'
                          : 'DK1',
                      municipality_code: parseInt(
                        result.adgangsadresse.kommune.kode,
                      ),
                    };
                  }
                } catch (error) {
                  console.error('Error fetching location data:', error);
                }
              }
            }}
            value={formatLocationString(locationData) || ''}
            disableField={false}
          />
        </Suspense>

        <Divider sx={{ my: 2 }} />
        <ListItemText
          sx={{ mb: 2 }}
          primary="Ugedage"
          secondary="Vælg hvilke dage i ugen, hvor energi forbrug forventes."
        />

        {updatedEnergyGroup?.options?.daysOfWeek && (
          <>
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                justifyContent: 'center',
                flexWrap: 'wrap',
                mb: 2,
              }}
            >
              {[
                { key: 'monday', label: 'M' },
                { key: 'tuesday', label: 'T' },
                { key: 'wednesday', label: 'O' },
                { key: 'thursday', label: 'T' },
                { key: 'friday', label: 'F' },
                { key: 'saturday', label: 'L' },
                { key: 'sunday', label: 'S' },
              ].map(({ key, label }) => (
                <ToggleButton
                  key={key}
                  value={key}
                  selected={updatedEnergyGroup?.options?.daysOfWeek[key]}
                  onChange={() => handleDayToggle(key)}
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 1,
                    fontWeight: 'bold',
                    p: 0,
                    '&.Mui-selected': {
                      backgroundColor: '#4CAF50',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: darken('#4CAF50', 0.1),
                      },
                    },
                  }}
                >
                  {label}
                </ToggleButton>
              ))}
            </Box>
            {!isAnyDaySelected && (
              <Typography variant="caption" color="error">
                Du skal vælge mindst én ugedag.
              </Typography>
            )}
          </>
        )}
        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <ListItemText
              sx={{ mb: 2 }}
              primary={'Vælg tidsrum'}
              secondary="Vælg et tidsrum, hvor energi forbrug forventes."
            />
          </Box>

          <Box sx={{ mx: 2 }}>
            <InvertedSlider
              isInverted={isInverted}
              min={0}
              max={23}
              step={1}
              value={range}
              onChange={handleChange}
              valueLabelDisplay="on"
              valueLabelFormat={formatHour}
              marks={[
                { value: 0, label: '0' },
                { value: 4, label: '4' },
                { value: 6, label: '6' },
                { value: 8, label: '8' },
                { value: 10, label: '10' },
                { value: 12, label: '12' },
                { value: 14, label: '14' },
                { value: 16, label: '16' },
                { value: 18, label: '18' },
                { value: 20, label: '20' },
                { value: 23, label: '23' },
              ]}
              disableSwap
              sx={{
                mt: 2,
                mb: 8,
                '& .MuiSlider-thumb': {
                  '&::after': {
                    content: '""',
                    display: 'none',
                  },
                },
              }}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose}>
          Afbryd
        </Button>
        <Button
          variant="contained"
          onClick={() =>
            onUpdate({
              ...updatedEnergyGroup,
              options: {
                ...updatedEnergyGroup.options,
              },
            }).then(() => {
              onClose();
            })
          }
          disabled={!updatedEnergyGroup?.name || !isAnyDaySelected}
        >
          Gem ændringer
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditEnergyGroupDialog;
